import { z } from "zod";
/**
 * Scheduling — Calendly-equivalent SSOT.
 *
 * Canon: docs/design-canon/04-shared/ssot.md (Zod first; routes
 * import these and use zValidator), docs/enhancements/feature-
 * sketches-2026-05.md §3.
 *
 * The pure slot-calc engine in
 * packages/api/src/services/scheduling-slots.service.ts works in
 * terms of the *Input* / *Slot* shapes here, so the same Zod schemas
 * drive both the API contract AND the engine's test fixtures.
 */
// ─── Common building blocks ──────────────────────────────
/** "HH:MM" 24-hour, e.g. "09:00", "17:30". */
export const TimeOfDaySchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Expected HH:MM (24-hour)");
/** IANA timezone string, e.g. "Europe/London". The list is too long
 *  to enum; we accept any non-empty bounded string and rely on the
 *  slot-calc engine's `Intl.DateTimeFormat({ timeZone })` round-trip
 *  to reject invalid zones at runtime. */
export const IanaTimezoneSchema = z.string().min(1).max(50);
/** Day-of-week. JS getDay() convention: Sunday=0, Saturday=6. */
export const DayOfWeekSchema = z.number().int().min(0).max(6);
// ─── Custom questions ─────────────────────────────────────
export const SchedulingQuestionKindSchema = z.enum(["short", "long", "choice"]);
export const SchedulingQuestionSchema = z
    .object({
    id: z.string().min(1).max(40),
    kind: SchedulingQuestionKindSchema,
    label: z.string().min(1).max(200),
    required: z.boolean(),
    options: z.array(z.string().min(1).max(100)).max(20).optional(),
})
    .refine((q) => q.kind !== "choice" || (q.options !== undefined && q.options.length >= 2), {
    message: "choice questions need at least 2 options",
});
// ─── Event types ──────────────────────────────────────────
export const EventTypeLocationKindSchema = z.enum(["teams", "phone", "in_person", "custom"]);
export const EventTypeSchema = z.object({
    id: z.string().uuid(),
    ownerEmail: z.string().email(),
    slug: z.string().min(1).max(80),
    name: z.string().min(1).max(200),
    description: z.string().nullable(),
    durationMinutes: z.number().int().min(5).max(480),
    locationKind: EventTypeLocationKindSchema,
    locationDetail: z.string().nullable(),
    bufferBeforeMinutes: z.number().int().min(0).max(240),
    bufferAfterMinutes: z.number().int().min(0).max(240),
    minNoticeMinutes: z
        .number()
        .int()
        .min(0)
        .max(60 * 24 * 7),
    maxAdvanceDays: z.number().int().min(1).max(365),
    dailyBookingLimit: z.number().int().min(1).max(50).nullable(),
    customQuestions: z.array(SchedulingQuestionSchema).max(10),
    active: z.boolean(),
    archivedAt: z.string().datetime().nullable(),
    archivedBy: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Slug rule: lowercase + digits + hyphen + underscore. Stricter than
 *  the DB column to keep public URLs predictable. */
export const EventTypeSlugSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9_-]+$/, "Slug must be lowercase letters, digits, hyphens, or underscores");
export const EventTypeCreateSchema = z.object({
    slug: EventTypeSlugSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    durationMinutes: z.number().int().min(5).max(480),
    locationKind: EventTypeLocationKindSchema.default("teams"),
    locationDetail: z.string().max(500).optional(),
    bufferBeforeMinutes: z.number().int().min(0).max(240).default(0),
    bufferAfterMinutes: z.number().int().min(0).max(240).default(0),
    minNoticeMinutes: z
        .number()
        .int()
        .min(0)
        .max(60 * 24 * 7)
        .default(60),
    maxAdvanceDays: z.number().int().min(1).max(365).default(60),
    dailyBookingLimit: z.number().int().min(1).max(50).nullable().optional(),
    customQuestions: z.array(SchedulingQuestionSchema).max(10).default([]),
    active: z.boolean().default(true),
});
export const EventTypeUpdateSchema = EventTypeCreateSchema.partial();
export const EventTypeListResponseSchema = z.object({
    eventTypes: z.array(EventTypeSchema),
});
// ─── Availability rules ──────────────────────────────────
export const AvailabilityRuleSchema = z.object({
    id: z.string().uuid(),
    ownerEmail: z.string().email(),
    dayOfWeek: DayOfWeekSchema,
    startTime: TimeOfDaySchema,
    endTime: TimeOfDaySchema,
    timezone: IanaTimezoneSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const AvailabilityRuleCreateSchema = z
    .object({
    dayOfWeek: DayOfWeekSchema,
    startTime: TimeOfDaySchema,
    endTime: TimeOfDaySchema,
    timezone: IanaTimezoneSchema,
})
    .refine((r) => r.startTime < r.endTime, {
    message: "startTime must be earlier than endTime",
    path: ["endTime"],
});
// ─── Availability overrides ──────────────────────────────
export const AvailabilityOverrideKindSchema = z.enum(["block", "extra"]);
/** YYYY-MM-DD. Match the date column wire format. */
export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
export const AvailabilityOverrideSchema = z.object({
    id: z.string().uuid(),
    ownerEmail: z.string().email(),
    date: IsoDateSchema,
    kind: AvailabilityOverrideKindSchema,
    startTime: TimeOfDaySchema.nullable(),
    endTime: TimeOfDaySchema.nullable(),
    timezone: IanaTimezoneSchema,
    reason: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const AvailabilityOverrideCreateSchema = z
    .object({
    date: IsoDateSchema,
    kind: AvailabilityOverrideKindSchema,
    startTime: TimeOfDaySchema.nullable().default(null),
    endTime: TimeOfDaySchema.nullable().default(null),
    timezone: IanaTimezoneSchema,
    reason: z.string().max(200).optional(),
})
    .refine((o) => 
// block-whole-day is allowed (start+end null); any other null is a
// partial pair which makes no sense.
(o.startTime === null && o.endTime === null) || (o.startTime !== null && o.endTime !== null), { message: "startTime and endTime must both be set, or both null" })
    .refine((o) => o.kind !== "extra" || (o.startTime !== null && o.endTime !== null), {
    message: "extra overrides require both startTime and endTime",
})
    .refine((o) => o.startTime === null || o.endTime === null || o.startTime < o.endTime, {
    message: "startTime must be earlier than endTime",
    path: ["endTime"],
});
// ─── Bookings ────────────────────────────────────────────
export const BookingStatusSchema = z.enum(["confirmed", "cancelled", "rescheduled", "no_show"]);
export const BookingSchema = z.object({
    id: z.string().uuid(),
    eventTypeId: z.string().uuid().nullable(),
    ownerEmail: z.string().email(),
    inviteeName: z.string().min(1).max(200),
    inviteeEmail: z.string().email(),
    inviteeTimezone: IanaTimezoneSchema,
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
    status: BookingStatusSchema,
    answers: z.record(z.string()),
    cancellationReason: z.string().nullable(),
    graphEventId: z.string().nullable(),
    joinUrl: z.string().nullable(),
    contactId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BookingCreateSchema = z.object({
    ownerSlug: z.string().min(1).max(80),
    eventTypeSlug: EventTypeSlugSchema,
    startAt: z.string().datetime(),
    inviteeName: z.string().min(1).max(200),
    inviteeEmail: z.string().email(),
    inviteeTimezone: IanaTimezoneSchema,
    answers: z.record(z.string().max(2000)).default({}),
    /** Cloudflare Turnstile token. Required when CF_TURNSTILE_SECRET is
     *  configured at the API; the route validates server-side. */
    turnstileToken: z.string().max(2048).optional(),
});
export const BookingCancelSchema = z.object({
    reason: z.string().max(500).optional(),
});
export const BookingListQuerySchema = z.object({
    scope: z.enum(["upcoming", "past", "all"]).default("all"),
});
// ─── Listing query (for owner admin) ─────────────────────
/** Query string for `GET /scheduling/availability/overrides`. */
export const AvailabilityOverrideListQuerySchema = z.object({
    fromDate: IsoDateSchema.optional(),
});
// ─── Public availability response ────────────────────────
/**
 * One bookable slot. The pure slot-calc engine emits these directly;
 * the API surfaces them on the public availability endpoint.
 *
 * `startAt`/`endAt` are absolute timestamps (UTC ISO8601). The
 * client renders them in the invitee's chosen timezone — the engine
 * does not pre-format because that's a presentation concern.
 */
export const SchedulingSlotSchema = z.object({
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
});
export const PublicAvailabilityResponseSchema = z.object({
    eventType: z.object({
        name: z.string(),
        description: z.string().nullable(),
        durationMinutes: z.number().int(),
        locationKind: EventTypeLocationKindSchema,
        customQuestions: z.array(SchedulingQuestionSchema),
    }),
    ownerDisplayName: z.string(),
    slots: z.array(SchedulingSlotSchema),
    /** The window the slots cover, inclusive of from, exclusive of to. */
    fromAt: z.string().datetime(),
    toAt: z.string().datetime(),
});
// ─── Slot-calc engine input contract ─────────────────────
/**
 * Pure-function input for the slot engine. Lives in `shared` so the
 * tests can import it from the same package the API uses, ensuring
 * one test case → one wire shape.
 *
 * Note: `existingBookings` here only carries (start, end). The owner-
 * email scope is the engine caller's responsibility, not the engine's.
 */
export const SlotCalcInputSchema = z.object({
    /** Slot length to emit. */
    durationMinutes: z.number().int().min(5).max(480),
    bufferBeforeMinutes: z.number().int().min(0),
    bufferAfterMinutes: z.number().int().min(0),
    minNoticeMinutes: z.number().int().min(0),
    maxAdvanceDays: z.number().int().min(1),
    dailyBookingLimit: z.number().int().min(1).nullable(),
    /** Inclusive window start — usually `now`. */
    fromAt: z.string().datetime(),
    /** Exclusive window end. */
    toAt: z.string().datetime(),
    /** Per-day-of-week + time-window bands the owner has marked open. */
    rules: z.array(z.object({
        dayOfWeek: DayOfWeekSchema,
        startTime: TimeOfDaySchema,
        endTime: TimeOfDaySchema,
        timezone: IanaTimezoneSchema,
    })),
    overrides: z.array(z.object({
        date: IsoDateSchema,
        kind: AvailabilityOverrideKindSchema,
        startTime: TimeOfDaySchema.nullable(),
        endTime: TimeOfDaySchema.nullable(),
        timezone: IanaTimezoneSchema,
    })),
    /** Owner's already-busy windows from existing bookings + Graph
     *  free/busy. Both kinds get unioned before the engine subtracts. */
    busy: z.array(z.object({
        startAt: z.string().datetime(),
        endAt: z.string().datetime(),
    })),
});
//# sourceMappingURL=scheduling.js.map